/**
 * AdmissionsPage (Phase-2, Module 4)
 *
 * Officer requests IPD admission, tracks the 8-status workflow, views the
 * admission timeline, and uploads admission documents. Coordinator-side
 * approvals live in AdmissionApprovalsPage.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, FormControl, InputLabel, Select, IconButton, Tooltip, Chip, Typography, Divider, Stack,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { LocalHospital as HospIcon, Add as AddIcon, Visibility as ViewIcon, Upload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, FileUpload, ConfirmDialog, OfficerSelector } from '../../components/common';
import { AdmissionApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import { ADMISSION_TYPES, labelFor } from '../../utils/phase2Constants';
import type { HospitalAdmission, AdmissionDocument, AdmissionTimelineEvent } from '../../types/phase2';
import { useCurrentOfficer } from '../../hooks/useCurrentOfficer';
import { useNavigate, useLocation } from 'react-router-dom';

const DOC_TYPES = ['ADMISSION_SLIP', 'ID_PROOF', 'MEDICAL_HISTORY', 'ESTIMATE', 'DISCHARGE_SUMMARY', 'FINAL_REPORT', 'OTHER'];

const AdmissionsPage: React.FC = () => {
  const { officerId } = useCurrentOfficer();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<HospitalAdmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    patientName: '', admissionType: 'PLANNED', hospitalName: '', hospitalAddress: '',
    reasonForAdmission: '', admittingDoctor: '', admissionDate: '', expectedDischargeDate: '',
    estimatedCost: '', emergencyContact: '', emergencyContactName: '', medicalHistory: '',
  });

  const [detail, setDetail] = useState<HospitalAdmission | null>(null);
  const [docOpen, setDocOpen] = useState(false);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docType, setDocType] = useState('ADMISSION_SLIP');
  const [docName, setDocName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [delDoc, setDelDoc] = useState<{ admissionId: number; docId: number; name: string } | null>(null);

  const load = useCallback(async () => {
    if (!officerId) return;
    setLoading(true); setError(null);
    try { setRows((await AdmissionApi.listByOfficer(officerId)) || []); }
    catch (e: any) { setError(e?.response?.data?.message || 'Failed to load admissions'); }
    finally { setLoading(false); }
  }, [officerId]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id: number) => {
    try { setDetail(await AdmissionApi.getById(id)); } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load admission'); }
  };

  const handleDeleteDoc = async () => {
    if (!delDoc) return;
    try {
      await AdmissionApi.deleteDocument(delDoc.admissionId, delDoc.docId);
      setDetail(await AdmissionApi.getById(delDoc.admissionId));
      setDelDoc(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Delete failed');
      setDelDoc(null);
    }
  };

  const handleSave = async () => {
    if (!officerId || !form.hospitalName || !form.reasonForAdmission) return;
    setSaving(true); setError(null);
    try {
      await AdmissionApi.create({
        officerId, patientName: form.patientName || undefined, admissionType: form.admissionType,
        hospitalName: form.hospitalName, hospitalAddress: form.hospitalAddress || undefined,
        reasonForAdmission: form.reasonForAdmission, admittingDoctor: form.admittingDoctor || undefined,
        admissionDate: form.admissionDate || undefined, expectedDischargeDate: form.expectedDischargeDate || undefined,
        estimatedCost: form.estimatedCost ? Number(form.estimatedCost) : undefined,
        emergencyContact: form.emergencyContact || undefined, emergencyContactName: form.emergencyContactName || undefined,
        medicalHistory: form.medicalHistory || undefined,
      });
      setOpen(false);
      setForm({ patientName: '', admissionType: 'PLANNED', hospitalName: '', hospitalAddress: '', reasonForAdmission: '', admittingDoctor: '', admissionDate: '', expectedDischargeDate: '', estimatedCost: '', emergencyContact: '', emergencyContactName: '', medicalHistory: '' });
      load();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to request admission'); }
    finally { setSaving(false); }
  };

  const handleUploadDoc = async () => {
    if (!detail || !docFile || !docName) return;
    setUploading(true); setError(null);
    try {
      await AdmissionApi.uploadDocument(detail.id, docFile, docType, docName);
      setDetail(await AdmissionApi.getById(detail.id));
      setDocOpen(false); setDocFile(null); setDocName(''); setDocType('ADMISSION_SLIP');
    } catch (e: any) { setError(e?.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const columns: GridColDef[] = [
    { field: 'admissionNumber', headerName: 'Admission No.', width: 150 },
    { field: 'hospitalName', headerName: 'Hospital', flex: 1.3, minWidth: 160 },
    { field: 'admissionType', headerName: 'Type', width: 120, renderCell: (p) => <Chip size="small" label={labelFor(ADMISSION_TYPES, p.value)} variant="outlined" /> },
    { field: 'admissionDate', headerName: 'Date', width: 120 },
    { field: 'status', headerName: 'Status', width: 150, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Actions', width: 110, sortable: false,
      renderCell: (p) => (
        <Tooltip title="View details"><IconButton size="small" onClick={() => openDetail(p.row.id)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
      ),
    },
  ];

  return (
    <PageScaffold title="Hospital Admissions" subtitle="Request & track IPD admissions" icon={<HospIcon />}
      actionLabel={officerId ? 'Request Admission' : undefined} actionIcon={<AddIcon />} onAction={officerId ? () => setOpen(true) : undefined}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      {!officerId && (
        <OfficerSelector onSelect={(id) => navigate(`${location.pathname}?officerId=${id}`)} />
      )}
      {officerId && (
      <>
      <Box sx={{ height: 520, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>
      </>
      )}

      {/* Create dialog */}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Request Hospital Admission</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Admission Type</InputLabel>
              <Select label="Admission Type" value={form.admissionType}
                onChange={(e) => setForm({ ...form, admissionType: e.target.value })}>
                {ADMISSION_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Patient Name" value={form.patientName} onChange={(e) => setForm({ ...form, patientName: e.target.value })} fullWidth />
            <TextField label="Hospital Name" required value={form.hospitalName} onChange={(e) => setForm({ ...form, hospitalName: e.target.value })} fullWidth />
            <TextField label="Hospital Address" value={form.hospitalAddress} onChange={(e) => setForm({ ...form, hospitalAddress: e.target.value })} fullWidth />
            <TextField label="Reason for Admission" required multiline minRows={2} value={form.reasonForAdmission} onChange={(e) => setForm({ ...form, reasonForAdmission: e.target.value })} fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Admitting Doctor" value={form.admittingDoctor} onChange={(e) => setForm({ ...form, admittingDoctor: e.target.value })} fullWidth />
              <TextField type="date" label="Admission Date" InputLabelProps={{ shrink: true }} value={form.admissionDate} onChange={(e) => setForm({ ...form, admissionDate: e.target.value })} fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField type="date" label="Expected Discharge" InputLabelProps={{ shrink: true }} value={form.expectedDischargeDate} onChange={(e) => setForm({ ...form, expectedDischargeDate: e.target.value })} fullWidth />
              <TextField label="Estimated Cost (₹)" type="number" value={form.estimatedCost} onChange={(e) => setForm({ ...form, estimatedCost: e.target.value })} fullWidth />
            </Box>
            <TextField label="Emergency Contact (Name / Phone)" value={`${form.emergencyContactName}`} placeholder="Name" onChange={(e) => setForm({ ...form, emergencyContactName: e.target.value })} fullWidth />
            <TextField label="Emergency Contact Phone" value={form.emergencyContact} onChange={(e) => setForm({ ...form, emergencyContact: e.target.value })} fullWidth />
            <TextField label="Medical History" multiline minRows={2} value={form.medicalHistory} onChange={(e) => setForm({ ...form, medicalHistory: e.target.value })} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!officerId || !form.hospitalName || !form.reasonForAdmission || saving}
            sx={{ textTransform: 'none', fontWeight: 600 }}>{saving ? 'Submitting...' : 'Submit Request'}</Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>{detail?.admissionNumber} — {detail?.hospitalName}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={`Type: ${labelFor(ADMISSION_TYPES, detail.admissionType)}`} variant="outlined" />
                <Chip label={`Status: ${detail.statusDisplayName || detail.status}`} color="primary" />
                {detail.admittingDoctor && <Chip label={`Doctor: ${detail.admittingDoctor}`} variant="outlined" />}
              </Box>
              {detail.reasonForAdmission && <Typography variant="body2"><strong>Reason:</strong> {detail.reasonForAdmission}</Typography>}
              <Divider><Typography variant="caption">TIMELINE</Typography></Divider>
              <Stack spacing={1}>
                {(detail.timeline || []).map((t: AdmissionTimelineEvent, i: number) => (
                  <Box key={i} sx={{ borderLeft: '3px solid #3B82F6', pl: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>{t.statusDisplayName || t.newStatus || t.status}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.eventDate} · {t.performedByName}</Typography>
                    {t.eventDescription && <Typography variant="body2">{t.eventDescription}</Typography>}
                  </Box>
                ))}
                {(!detail.timeline || detail.timeline.length === 0) && <Typography variant="body2" color="text.secondary">No timeline events yet.</Typography>}
              </Stack>
              <Divider><Typography variant="caption">DOCUMENTS</Typography></Divider>
              <Stack spacing={1}>
                {(detail.documents || []).map((d: AdmissionDocument, i: number) => {
                  const url = toAbsoluteFileUrl(d.fileUrl);
                  return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{d.documentName || d.documentType} <Chip size="small" label={d.documentType} sx={{ ml: 1 }} /></Typography>
                      <Box>
                        {url && <Button size="small" href={url} target="_blank" rel="noopener noreferrer">View</Button>}
                        <Tooltip title="Delete document">
                          <IconButton size="small" color="error" onClick={() => setDelDoc({ admissionId: detail.id!, docId: d.id!, name: d.documentName || d.documentType || 'Document' })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
                {(!detail.documents || detail.documents.length === 0) && <Typography variant="body2" color="text.secondary">No documents uploaded.</Typography>}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetail(null)}>Close</Button>
          <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setDocOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}>Upload Document</Button>
        </DialogActions>
      </Dialog>

      {/* Document upload dialog */}
      <Dialog open={docOpen} onClose={() => !uploading && setDocOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Admission Document</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select label="Document Type" value={docType} onChange={(e) => setDocType(e.target.value)}>
                {DOC_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Document Name" required value={docName} onChange={(e) => setDocName(e.target.value)} fullWidth />
            <FileUpload onFileSelected={setDocFile} uploading={uploading} label="Select document (PDF / image)" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDocOpen(false)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadDoc} disabled={!docFile || !docName || uploading}
            sx={{ textTransform: 'none', fontWeight: 600 }}>{uploading ? 'Uploading...' : 'Upload'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={delDoc != null}
        title="Delete admission document?"
        message={delDoc ? `This will permanently remove "${delDoc.name}" and its file. This action cannot be undone.` : ''}
        confirmLabel="Delete"
        onConfirm={handleDeleteDoc}
        onClose={() => setDelDoc(null)}
      />
    </PageScaffold>
  );
};

export default AdmissionsPage;